import React, { useEffect } from "react";
import styled from "styled-components";
import * as Slider from "@radix-ui/react-slider";
import TextButton from "@common/ui/TextButton";
import ToggleGroup from "@common/ui/ToggleGroup";
import { useFilters } from "@lib/Filtering/useFilters";
import { useStore } from "@state/store";

export const formatPrice = (val: number) => {
  const formattedVal = Number(val).toLocaleString();
  return formattedVal;
};

// TODO - Should come from payload
const marketingNames = {
  House: "House",
  Apartment: "Apartment",
};

const bedroomDisplayNames = {
  "0": "Studio",
  "1": "1 Bed",
  "2": "2 Bed",
  "3": "3 Bed",
  "4": "4 Bed",
};

type ResidenceFiltersProps = {
  className?: string;
};

export type TFilters = {
  topologies: string[];
  beds: string[];
  price: [number, number];
  floor: [number, number];
};

const Filters: React.FC<ResidenceFiltersProps> = ({ className }) => {
  const syncState = useStore((s) => s.syncState);
  const {
    dynamicFilters,
    handleToggleChange,
    handleRangeChange,
    filters,
    handleReset,
  } = useFilters();

  const { floorRange, priceRange, bedOptions } = dynamicFilters;

  const [localPrice, setLocalPrice] = React.useState(null);
  const [localFloor, setLocalFloor] = React.useState(null);

  useEffect(() => {
    setLocalPrice(priceRange);
    setLocalFloor(floorRange);
  }, [priceRange]);

  return (
    <FiltersWrapper className={className}>
      {floorRange?.[1] === 0 || !localPrice ? <div>Loading...</div> : (
        <>
          <div className="price">
            {!isNaN(priceRange[0]) && (
              <>
                <div className="label">Price</div>
                <div className="filter">
                  <div className="price-values">
                    <div className="price-value">
                      £{formatPrice(localPrice[0])}
                    </div>
                    <div className="price-value">
                      £{formatPrice(localPrice[1])}
                    </div>
                  </div>
                  <form className="slider">
                    <Slider.Root
                      value={localPrice}
                      className="SliderRoot"
                      step={10000}
                      min={priceRange[0]}
                      max={priceRange[1]}
                      onValueChange={(value) => {
                        setLocalPrice(value as [number, number]);
                      }}
                      onValueCommit={(value) => {
                        handleRangeChange("price", value);
                      }}
                    >
                      <Slider.Track className="SliderTrack">
                        <Slider.Range className="SliderRange" />
                      </Slider.Track>
                      <Slider.Thumb
                        className="SliderThumb"
                        aria-label="Volume"
                      />
                      <Slider.Thumb
                        className="SliderThumb"
                        aria-label="Volume"
                      />
                    </Slider.Root>
                  </form>
                </div>
              </>
            )}
          </div>
          <div className="floors">
            <div className="label">Floor</div>
            <div className="filter">
              <div className="floor-values">
                <div className="floor-value">{localFloor[0]}</div>
                <div className="floor-value">{localFloor[1]}</div>
              </div>
              <form>
                <Slider.Root
                  className="SliderRoot"
                  value={localFloor}
                  minStepsBetweenThumbs={1.5}
                  step={1}
                  min={floorRange[0]}
                  max={floorRange[1]}
                  onValueChange={(value) => {
                    setLocalFloor(value as [number, number]);
                  }}
                  onValueCommit={(value) => {
                    handleRangeChange("floor", value);
                  }}
                >
                  <Slider.Track className="SliderTrack">
                    <Slider.Range className="SliderRange" />
                  </Slider.Track>
                  <Slider.Thumb className="SliderThumb" />
                  <Slider.Thumb className="SliderThumb" />
                </Slider.Root>
              </form>
            </div>
          </div>
          <div className="beds">
            <div className="label">Bedrooms</div>
            <ToggleGroup
              labels={bedroomDisplayNames}
              options={bedOptions}
              selected={filters.beds}
              type="beds"
              onClick={(value: string) => {
                handleToggleChange("beds", value, false);
              }}
            />
          </div>
          <TextButton
            className="reset"
            variant="fill"
            onClick={() => {
              syncState({ selectedUnit: null });
              setLocalPrice(priceRange);
              handleReset();
            }}
          >
            Reset Filters
          </TextButton>
        </>
      )}
    </FiltersWrapper>
  );
};
export default Filters;

const FiltersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2rem;
  width: 100%;
  height: 100%;
  gap: 1rem;
  color: var(--black);
  overflow-y: auto;

  ::-webkit-scrollbar {
    display: none;
  }

  .label {
    font-family: primaryFont;
    font-size: 0.75rem;
    text-transform: uppercase;
    padding: 0.5rem 0;
    letter-spacing: 0.15em;
    color: var(--black);
  }

  .floors,
  .price,
  .beds,
  .buildings {
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 0;
  }

  .SliderRoot {
    position: relative;
    display: flex;
    align-items: center;
    user-select: none;
    touch-action: none;
    height: 4rem;
  }

  .SliderTrack {
    background-color: var(--accent);
    position: relative;
    flex-grow: 1;
    height: 1px;
  }

  .SliderRange {
    background-color: var(--black);
    position: absolute;
    height: 1px;
  }

  .SliderThumb {
    cursor: pointer;
    display: block;
    width: 16px;
    height: 16px;
    background-color: var(--black);
  }

  .floors .filter,
  .price .filter {
    color: var(--black);
  }

  .baths .filter,
  .buildings .filter,
  .beds .filter,
  .view .filter {
    width: 100%;
    display: flex;
    font-weight: bold;
    color: var(--black);
    border-radius: 5px;
    overflow: hidden;
    border: 1px solid var(--primary);

    button {
      all: unset;
    }

    .option {
      box-sizing: border-box;
      width: 100%;
      text-align: center;
      padding: 0.75rem 0;
      font-size: 1rem;
      cursor: pointer;
      transition: 0.3s;
    }

    .option:not(:last-child) {
      border-right: 1px solid var(--primary);
      transition: 0.3s;
    }

    .option.selected {
      background-color: var(--black);
      color: white;
      transition: 0.3s;
    }
  }

  .floors .floor-values,
  .price .price-values {
    font-size: 1.25rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .reset {
    padding: 1rem;
    border-radius: 5px;
    border: var(--black) solid 1px;
    background-color: var(--primary);
    color: var(--black);
  }
`;
